import {
  Cartesian3,
  Matrix3,
  Matrix4,
  Viewer,
  Math as CesiumMath,
} from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { isMacOs } from "lib/actions/isMacOs";

// flyToCameraTransform scratch variables
const scratchRotationMatrix = new Matrix3();
const scratchDestination = new Cartesian3();
const scratchDirection = new Cartesian3();
const scratchUp = new Cartesian3();

export default class CameraHistoryPlugin implements AttachablePluginInterface {
  _viewer: Viewer | null = null;
  _undoHistory: Matrix4[] = [];
  _redoHistory: Matrix4[] = [];
  _undergroundIndicator: HTMLDivElement | null = null;
  _isUnderground: boolean = false;

  constructor() {
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleCameraMoveEnd = this.handleCameraMoveEnd.bind(this);
  }

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
    this._viewer.scene.cameraEventWaitTime = 100;
    this._viewer.camera.moveEnd.addEventListener(this.handleCameraMoveEnd);
    document.addEventListener("keydown", this.handleKeyDown);
  }

  detachViewer() {
    if (!this._viewer) return;

    if (this._undergroundIndicator) {
      this._undergroundIndicator.remove();
      this._undergroundIndicator = null;
    }

    this._viewer.scene.cameraEventWaitTime = 500; // cesium default
    this._viewer.camera.moveEnd.removeEventListener(this.handleCameraMoveEnd);
    document.removeEventListener("keydown", this.handleKeyDown);
    this._viewer = null;
  }

  get isUnderground(): boolean {
    return this._isUnderground;
  }

  private handleCameraMoveEnd() {
    if (!this._viewer) return;
    const newTransform = Matrix4.clone(this._viewer.camera.inverseViewMatrix);

    const wasUnderground = this._isUnderground;
    this._isUnderground = this._viewer.scene.cameraUnderground;

    // Handle underground/overground transitions
    if (this._isUnderground !== wasUnderground) {
      if (this._isUnderground) {
        this.showCameraUndergroundIndicator();
      } else {
        this.hideCameraUndergroundIndicator();
      }
    }

    /**
     * The cameraMoveEnd event is triggered in a number of instances outside
     * of manual camera movement. If the latest view matrix is similar to the
     * most recent in the undo history, don't add it to the array. Otherwise,
     * the `undo()` method will need to be called an additional time in order
     * to achieve the desired effect.
     */
    if (
      newTransform.equalsEpsilon(
        this._undoHistory[this._undoHistory.length - 1],
        CesiumMath.EPSILON4,
      )
    ) {
      return;
    }

    this._undoHistory.push(newTransform);
    // Clear out the redo history when a new manual camera movement event occurs.
    this._redoHistory = [];
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (
      (event.ctrlKey || event.metaKey) &&
      !event.shiftKey &&
      event.key.toLowerCase() === "z"
    ) {
      this.undo();
    } else if (
      (event.ctrlKey || event.metaKey) &&
      event.shiftKey &&
      event.key.toLowerCase() === "z"
    ) {
      this.redo();
    }
  }

  private undo() {
    if (!this._viewer || this._undoHistory.length < 2) return;

    const currentTransform = this._undoHistory.pop();
    this._redoHistory.push(currentTransform);

    const previousTransform = this._undoHistory[this._undoHistory.length - 1];
    this.flyToCameraTransform(previousTransform);
  }

  private redo() {
    if (!this._viewer || this._redoHistory.length === 0) return;

    const nextTransform = this._redoHistory.pop();
    this.flyToCameraTransform(nextTransform);
  }

  private flyToCameraTransform(matrix: Matrix4) {
    if (!this._viewer) return;

    const rotationMatrix = Matrix4.getMatrix3(matrix, scratchRotationMatrix);
    const destination = Matrix4.getTranslation(matrix, scratchDestination);
    const direction = Matrix3.getColumn(rotationMatrix, 2, scratchDirection);
    Cartesian3.negate(direction, direction);
    const up = Matrix3.getColumn(rotationMatrix, 1, scratchUp);

    this._viewer.camera.flyTo({
      destination,
      orientation: {
        direction,
        up,
      },
      duration: 1,
    });
  }

  private showCameraUndergroundIndicator() {
    if (this._undergroundIndicator) return;
    const isMac = isMacOs();
    const indicator = document.createElement("div");
    Object.assign(indicator.style, {
      position: "absolute",
      bottom: "0.5rem",
      right: "0.5rem",
      padding: "0.25rem 0.5rem",
      background: "rgb(0, 0, 0, 0.7)",
      color: "#fff",
      borderRadius: "0.5rem",
      fontSize: ".875rem",
    });
    indicator.innerHTML = `Press ${isMac ? "<kbd>Cmd</kbd>" : "<kbd>Ctrl</kbd>"} + <kbd>Z</kbd> to return to previous camera position`;
    this._viewer.container.appendChild(indicator);
    this._undergroundIndicator = indicator;
  }

  private hideCameraUndergroundIndicator() {
    if (!this._undergroundIndicator) return;
    this._undergroundIndicator.remove();
    this._undergroundIndicator = null;
  }
}
